import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Video, Square, Download } from "lucide-react";

/**
 * LiveRecorder — Bigo-style screen recorder button. Uses the browser's
 * MediaRecorder API to capture the LIVE room in real time (either the
 * host's own composition via `getDisplayMedia`, or the on-screen
 * `<video>` element the LiveKit SDK is rendering — whichever the
 * browser will hand us).
 *
 * Design choices:
 *   * No server upload path yet — creator gets a `.webm` download link
 *     when they hit Stop. Keeps this feature dependency-free while we
 *     figure out cost. If they want the clip on their feed, they can
 *     re-upload it via the existing /upload flow.
 *   * Recording indicator is a small red pulsing chip that only the
 *     recorder sees (not viewers).
 *   * Stops automatically if the underlying MediaStream ends (e.g., the
 *     user cancels the screen picker).
 */

export default function LiveRecorder() {
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [state, setState] = useState("idle");        // idle | recording | ready
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [size, setSize] = useState(0);

  // Same platform gate as LiveScreenShare — mobile browsers don't
  // support getDisplayMedia, so hide the button entirely instead of
  // showing an error toast every time it's tapped.
  const supported = typeof navigator !== "undefined"
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === "function"
    && typeof window !== "undefined"
    && typeof window.MediaRecorder !== "undefined";

  const start = useCallback(async () => {
    if (state === "recording") return;
    if (downloadUrl) { URL.revokeObjectURL(downloadUrl); setDownloadUrl(null); }
    try {
      // Prefer displayMedia so the whole live room (video + gifts + chat
      // overlay) is captured. Fall back to whatever the browser will give.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
            ? "video/webm;codecs=vp8,opus"
            : "video/webm");
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 3_000_000 });
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) {
          chunksRef.current.push(e.data);
          setSize((s) => s + e.data.size);
        }
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        setState("ready");
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      // If the user stops the screen-share via the browser UI, close cleanly.
      stream.getVideoTracks().forEach((t) => {
        t.addEventListener("ended", () => { try { rec.stop(); } catch { /* already stopped */ } });
      });
      rec.start(1000);   // 1s timeslices → lets `ondataavailable` fire periodically
      recRef.current = rec;
      setSize(0);
      setState("recording");
      toast.success("Recording started");
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError") toast.error("Screen share was cancelled");
      else toast.error(err?.message || "Couldn't start recording");
    }
  }, [state, downloadUrl]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  const humanSize = size < 1024 * 1024
    ? `${(size / 1024).toFixed(0)} KB`
    : `${(size / 1024 / 1024).toFixed(1)} MB`;

  if (!supported) return null;

  if (state === "recording") {
    return (
      <button
        onClick={stop}
        data-testid="live-recorder-stop"
        className="p-2 bg-[#FF3B3B] text-white transition-colors flex items-center gap-1 shadow-[0_0_16px_rgba(255,59,59,0.55)] animate-pulse"
        aria-label="Stop recording"
        title={`Recording — ${humanSize}`}
      >
        <Square className="w-4 h-4" fill="currentColor" />
        <span className="text-[10px] font-mono uppercase tracking-widest hidden sm:inline">
          rec
        </span>
      </button>
    );
  }

  if (state === "ready" && downloadUrl) {
    return (
      <a
        href={downloadUrl}
        download={`live-recording-${Date.now()}.webm`}
        onClick={() => setTimeout(() => setState("idle"), 500)}
        data-testid="live-recorder-download"
        className="p-2 bg-[#39FF14] text-black transition-colors flex items-center gap-1 shadow-[0_0_16px_rgba(57,255,20,0.55)]"
        aria-label="Download recording"
        title={`Download ${humanSize}`}
      >
        <Download className="w-4 h-4" />
        <span className="text-[10px] font-mono uppercase tracking-widest hidden sm:inline">
          save
        </span>
      </a>
    );
  }

  return (
    <button
      onClick={start}
      data-testid="live-recorder-start"
      className="p-2 bg-white/10 hover:bg-white/20 text-white/80 transition-colors"
      aria-label="Start recording"
      title="Record this live"
    >
      <Video className="w-4 h-4" />
    </button>
  );
}
